import React, { useCallback, useMemo } from 'react';
import { omit, sort } from 'ramda';
import { useParams } from 'react-router-dom';
import { Stack, Checkbox, Overlay, Text, Input, Select, LoadingBalls } from '@moda/om';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { reportError } from '../../../lib/reportError';
import { RETURN_REASONS } from '../../../constants';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { OrderItem } from '../../../hooks/useOrder';
import { useReturnSummary, useUpdateReturnSummaryMutation } from '../../../hooks/useReturnSummary';
import { AccountOrderItem } from '../../OrderItem/AccountOrderItem';
import { useFineJewelryReturnMessageFlag } from '../../../hooks/useFineJewelryReturnMessageFlag';

import './RequestReturnEligibleItem.scss';

type Props = {
  item: OrderItem;
};

type ReturnReason = (typeof RETURN_REASONS)[number];

const isSizeAndFitReason = (reason: ReturnReason) =>
  ['64', '32', '2147483648'].includes(reason.value);

const reasonComparator = (left: ReturnReason, right: ReturnReason) => {
  if (isSizeAndFitReason(left)) {
    return isSizeAndFitReason(right) ? 0 : 1;
  }
  return -1;
};

export const RequestReturnEligibleItem: React.FC<Props> = ({ item }) => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { showErrorModal } = useErrorModal();
  const { returnSummary } = useReturnSummary({ orderNumber });
  const [updateReturnSummary, { loading }] = useUpdateReturnSummaryMutation(orderNumber);

  const shouldReorderReturnReasons = useFeatureFlag('reorder-return-reasons', [
    { default: false },
    { if: true, then: true }
  ]);

  const returnReasons = shouldReorderReturnReasons
    ? sort(reasonComparator, RETURN_REASONS)
    : RETURN_REASONS;

  const selectedItemIds = useMemo(
    () => returnSummary?.attributes.selectedItemIds || [],
    [returnSummary]
  );
  const isSelected = selectedItemIds.includes(item.id);

  const handleSelect = useCallback(async () => {
    try {
      await updateReturnSummary({
        selectedItemIds: isSelected
          ? selectedItemIds.filter(id => id !== item.id)
          : [...selectedItemIds, item.id],
        returnReasonHash:
          isSelected && returnSummary?.attributes.returnReasonHash
            ? omit([item.id], returnSummary.attributes.returnReasonHash)
            : returnSummary?.attributes.returnReasonHash
      });
    } catch (error) {
      reportError('REQUEST_RETURN_SELECT_ITEM', error);
      showErrorModal();
    }
  }, [updateReturnSummary, selectedItemIds, isSelected, showErrorModal, item, returnSummary]);

  const handleChangeReason = useCallback(
    async (reason: string) => {
      try {
        await updateReturnSummary({
          returnReasonHash: {
            ...returnSummary?.attributes.returnReasonHash,
            [item.id]: reason
          }
        });
      } catch (error) {
        reportError('REQUEST_RETURN_CHANGE_REASON', error);
        showErrorModal();
      }
    },
    [updateReturnSummary, returnSummary, item, showErrorModal]
  );

  const showMessage = useFineJewelryReturnMessageFlag();

  const isFineJewelry = item?.attributes.category === 'FINE_JEWELRY';

  if (!returnSummary) return null;

  return (
    <div className="RequestReturnEligibleItem">
      <div className="RequestReturnEligibleItem__content">
        <Stack
          className="RequestReturnEligibleItem__main"
          direction="horizontal"
          space={5}
          alignItems="center"
        >
          {showMessage ? (
            <>
              {!isFineJewelry && <Checkbox checked={isSelected} onChange={handleSelect} />}
              {isFineJewelry && <Checkbox disabled />}
            </>
          ) : (
            <Checkbox checked={isSelected} onChange={handleSelect} />
          )}
          <AccountOrderItem item={item} showExtraDetails shouldHideCustomJewelryMessage />
        </Stack>
        <div className="RequestReturnEligibleItem__reason">
          {isSelected && (
            <Input label="Reason for Return">
              <Select
                label="Select"
                options={returnReasons.map(reason => ({
                  value: reason.value,
                  label: reason.label
                }))}
                value={returnSummary.attributes.returnReasonHash[item.id]}
                onChange={handleChangeReason}
              />
            </Input>
          )}
        </div>
      </div>

      <Overlay show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </div>
  );
};
